import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Spinner } from '@/components/shared/UIComponents';
import { AuthFooter } from '@/components/auth/AuthFooter';

export function ResetPasswordPage() {
  const { updatePassword, session } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // If no session, the reset link may have expired or is invalid
  const [noSession, setNoSession] = useState(false);

  useEffect(() => {
    // Give Supabase a moment to process the recovery token from the URL
    const timer = setTimeout(() => {
      if (!session) setNoSession(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, [session]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await updatePassword(password);
      setSuccess(true);
      setTimeout(() => navigate('/admin'), 2000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-50 flex flex-col">
      <div className="flex-1 flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-10">
          <img src="/logo.svg" alt="DiscoveryGem" className="w-9 h-9 dark:brightness-0 dark:invert" />
          <div>
            <div className="font-semibold text-surface-900">DiscoveryGem</div>
            <div className="text-[10px] text-surface-500 uppercase tracking-widest">Platform</div>
          </div>
        </div>

        {success ? (
          <div>
            <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center mb-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-emerald-600 dark:text-emerald-400" strokeWidth="2">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-surface-900 mb-2">Password updated</h1>
            <p className="text-sm text-surface-500 mb-6">
              Your password has been reset successfully. Redirecting to dashboard...
            </p>
            <Link to="/admin" className="btn-primary w-full justify-center py-3 inline-flex">Go to Dashboard</Link>
          </div>
        ) : noSession && !session ? (
          <div>
            <div className="w-14 h-14 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center mb-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-red-600 dark:text-red-400" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-surface-900 mb-2">Invalid or expired link</h1>
            <p className="text-sm text-surface-500 mb-6">
              This password reset link is no longer valid. Please request a new one.
            </p>
            <Link to="/auth/forgot-password" className="btn-primary w-full justify-center py-3 inline-flex">
              Request New Link
            </Link>
            <p className="text-sm text-surface-500 text-center mt-6">
              <Link to="/auth/login" className="text-brand-600 hover:text-brand-700 font-medium transition-colors">
                Back to Sign In
              </Link>
            </p>
          </div>
        ) : !session ? (
          <div className="flex flex-col items-center py-12">
            <Spinner size="lg" />
            <p className="text-sm text-surface-500 mt-4">Verifying reset link...</p>
          </div>
        ) : (
          <div>
            <h1 className="text-2xl font-bold text-surface-900 mb-2">Set new password</h1>
            <p className="text-sm text-surface-500 mb-8">Choose a strong password for your account</p>

            {error && (
              <div className="mb-6 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-sm text-red-600 dark:text-red-400">{error}</div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="input-label">New Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="input-field pr-10"
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoComplete="new-password"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-500 hover:text-surface-700 transition-colors"
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <div>
                <label className="input-label">Confirm Password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="input-field"
                  placeholder="Repeat your password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full py-3">
                {loading ? <Spinner size="sm" /> : 'Reset Password'}
              </button>
            </form>

            <p className="text-sm text-surface-500 text-center mt-8">
              <Link to="/auth/login" className="text-brand-600 hover:text-brand-700 font-medium transition-colors">
                Back to Sign In
              </Link>
            </p>
          </div>
        )}
      </div>
      </div>
      <AuthFooter />
    </div>
  );
}
